import React, { useState, useCallback } from 'react';
import Box from '@material-ui/core/Box';
import { AuthInputField } from 'features/auth/components/AuthInputField';
import { BasePasswordInputField } from 'features/auth/components/BasePasswordInputField';
import Typography from '@material-ui/core/Typography';
import { Link } from 'react-router-dom';
import { authPaths } from 'features/auth/routes/paths';
import { Buttons } from 'shared';
import { FormProvider, useForm } from 'react-hook-form';
import { useStyles } from 'features/auth/components/Login/styles';
import { useTranslation } from 'hooks/useTranslation';
import useAuth from 'features/auth/hooks/useAuth';
import { useValidation } from 'hooks';
import { LoginInput } from 'features/auth/ts';

const INITIAL_FORM_STATE: LoginInput = {
  email: '',
  password: '',
};

const LoginForm = ({ onSuccessLogin }: { onSuccessLogin: (data: LoginInput) => void }) => {
  const classes = useStyles();
  const { t } = useTranslation('auth');
  const { onLogin, isLoading } = useAuth();
  const [emailIsEntered, setEmailIsEntered] = useState<boolean>(false);
  const { required, password, email } = useValidation();

  const form = useForm<LoginInput>({
    mode: 'onSubmit',
    defaultValues: INITIAL_FORM_STATE,
  });

  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { isSubmitting },
  } = form;

  const onSubmit = ({ email, password }: LoginInput) => {
    onLogin(email, password, () => onSuccessLogin({ email, password }));
  };

  const onSubmitButtonClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!emailIsEntered) {
        e.preventDefault();
        e.stopPropagation();
        const isEmailValid = await trigger('email');
        if (isEmailValid) setEmailIsEntered(true);
      }
    },
    [emailIsEntered, trigger]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" className={classes.loginFormContent}>
          {!emailIsEntered ? (
            <AuthInputField
              required
              type="email"
              name="email"
              control={control}
              fullWidth
              placeholder={t('yourAddressEmailPlaceholder')}
              label={t('email')}
              className={classes.emailInputField}
              rules={{
                validate: {
                  ...required,
                  ...email,
                },
              }}
            />
          ) : (
            <>
              <Box className={classes.passwordInputWrapper}>
                <i
                  className={classes.arrow}
                  onClick={() => {
                    setEmailIsEntered(false);
                  }}
                />
                <BasePasswordInputField
                  autoFocus
                  name="password"
                  control={control}
                  fullWidth
                  placeholder={t('enterYourPassword')}
                  label={t('password')}
                  rules={{
                    validate: {
                      ...required,
                      ...password,
                    },
                  }}
                />
              </Box>
              <Typography>
                <Link
                  to={`${authPaths.static.forgotPassword}?email=${encodeURIComponent(getValues('email'))}`}
                  className={classes.forgotPassword}
                >
                  {t('forgotPasswordQuestion')}
                </Link>
              </Typography>
            </>
          )}
        </Box>
        <Box className={classes.loginButtonWrapper}>
          <Buttons.Auth
            fullWidth
            onClick={onSubmitButtonClick}
            disabled={isSubmitting}
            title={emailIsEntered ? t('loginButtonText') : t('next', { ns: 'common' })}
            variant="outlined"
            color="primary"
            loading={isLoading}
            classes={{ label: classes.loginBtnLabel, root: classes.loginBtn }}
          />
        </Box>
      </form>
      <Typography className={classes.textNearToSignupLink}>
        {t('dontHaveAnAccount')}{' '}
        <Link className={classes.signupLink} to={authPaths.static.signUp}>
          {t('signUpHere')}
        </Link>
      </Typography>
    </FormProvider>
  );
};

export default LoginForm;
